import {
  ConjointQuestion,
  FreeText,
  Matrix,
  MultipleChoice,
  Ranked,
  SingleChoice,
  Slider,
  TextHighlighter,
  TextInstructions
} from '@focaldata/cin-ui-components'
import MatrixItem from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/MatrixItem'
import QuestionItem from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/QuestionItem'
import TextCardItem from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Common/Model/TextCardItem'
import { ConjointItem } from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Conjoint/ConjointItem.types'
import { TextHighlighterItem } from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/TextHighlighter/TextHighlighter.types'
import React, { useEffect, useMemo, useState } from 'react'
import {
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  EntryType,
  QuestionSettingCode,
  SettingValue
} from '../../../data/model/questionnaire'
import useTransformDraftQuestionnaireToPreviewData, {
  getConjointLoc,
  getFreeTextLoc,
  getMatrixMultiChoiceLoc,
  getMultiChoiceLoc,
  getRankedLoc,
  getTextInstructionsLoc,
  loc,
  scaleLoc
} from '../../../hooks/questionnaire/useTransformDraftQuestionnaireToPreviewData'
import {
  checkIfFreeText,
  checkIfMultipleChoice,
  checkIfRankedChoice,
  checkIfScale,
  checkIfSingleChoice,
  isSettingDisabled,
  isSettingEnabled
} from '../../../utils/questionnaireUtils'

// useTransformDraftQuestionnaireToPreviewData produces a preview item per
// renderable entry and silently skips these structural types, so the transform
// output would otherwise not be index-aligned with the entries array. Filtering
// them out up front keeps the two aligned (and keeps the walkthrough's
// progress/total honest — forks aren't questions a respondent steps through).
const NON_RENDERABLE_ENTRY_TYPES: ReadonlyArray<EntryType> = [
  EntryType.ForkEntryType,
  EntryType.SectionEntryType
]

interface Progress {
  current: number
  total: number
}
interface Next {
  disabled: boolean
  loading: boolean
  onNext: () => void
}

interface Props {
  // The (already flattened, and translation-overlaid) entries to render.
  entries: DraftQuestionnaireEntry[] | undefined
  // When set, the walkthrough opens at this entry (the in-editor side panel
  // jumps to the question being edited).
  focusEntryId?: string | null
}

/**
 * The survey preview renderer: it transforms draft entries into
 * respondent-facing preview items and renders the matching cin-ui-components
 * SurveyQuestion for each, one card at a time (the walkthrough used by the
 * in-editor side panel).
 */
const PreviewQuestions: React.FC<Props> = ({ entries, focusEntryId }) => {
  // Only renderable entries reach the transform, so its output stays 1:1 with
  // this list — renderEntry relies on that positional alignment.
  const renderableEntries = useMemo(
    () =>
      (entries ?? []).filter(
        (entry) => !NON_RENDERABLE_ENTRY_TYPES.includes(entry.entryType)
      ),
    [entries]
  )
  const { previewEntries: flattenPreviewEntries } =
    useTransformDraftQuestionnaireToPreviewData(renderableEntries)

  const focusIndex = Math.max(
    flattenPreviewEntries.findIndex(
      (previewEntry) => previewEntry.id === focusEntryId
    ),
    0
  )
  const [currentIndex, setCurrentIndex] = useState<number>(focusIndex)

  useEffect(() => {
    setCurrentIndex(focusIndex)
  }, [focusIndex])

  const handleOnNext: (currentIndex: number) => void = (currentIndex) => {
    if (currentIndex !== renderableEntries.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  // @todo Legacy eslint violation – fix this when editing
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const total = flattenPreviewEntries?.length || 0

  const renderEntry = (
    entry: DraftQuestionnaireEntry,
    index: number,
    progress: Progress,
    next: Next
  ): JSX.Element | null => {
    const previewEntry = flattenPreviewEntries[index]?.item
    if (entry.entryType === EntryType.QuestionEntryType) {
      // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if ((previewEntry as QuestionItem)?.question) {
        const entryItem = entry.entryItem as DraftQuestionItem
        if (checkIfSingleChoice(entryItem)) {
          return (
            <SingleChoice
              item={previewEntry as QuestionItem}
              progress={progress}
              loc={loc}
              next={next}
              isPreviewPanel
            />
          )
        }
        if (checkIfMultipleChoice(entryItem.settingValues)) {
          return (
            <MultipleChoice
              item={previewEntry as QuestionItem}
              progress={progress}
              loc={getMultiChoiceLoc(entryItem)}
              next={next}
              isPreviewPanel
            />
          )
        }
        if (checkIfRankedChoice(entryItem)) {
          return (
            <Ranked
              item={previewEntry as QuestionItem}
              progress={progress}
              loc={getRankedLoc(entryItem)}
              next={next}
              isPreviewPanel
            />
          )
        }
        if (checkIfFreeText(entryItem)) {
          const type = entryItem.settingValues.find(
            (sv) => sv.code === QuestionSettingCode.ContentStructure
          )
          const isCompact =
            type &&
            (type.value === SettingValue.Numeric1 ||
              type.value === SettingValue.PostCode ||
              type.value === SettingValue.Email)
          const numericOnly = type && type.value === SettingValue.Numeric1
          return (
            <FreeText
              item={previewEntry as QuestionItem}
              settings={{
                isCompact,
                numericOnly
              }}
              progress={progress}
              loc={getFreeTextLoc()}
              next={next}
              isPreviewPanel
            />
          )
        }
        if (checkIfScale(entryItem)) {
          const shouldShowLabels = isSettingEnabled(
            entryItem.settingValues,
            QuestionSettingCode.SliderLabels
          )
          const shouldShowTracker = isSettingDisabled(
            entryItem.settingValues,
            QuestionSettingCode.HideTracker
          )
          const shouldShowSelectedValue = isSettingDisabled(
            entryItem.settingValues,
            QuestionSettingCode.HideSelectedSliderValue
          )

          return (
            <Slider
              shouldShowLabels={shouldShowLabels}
              shouldShowTracker={shouldShowTracker}
              shouldShowSelectedValue={shouldShowSelectedValue}
              item={previewEntry as QuestionItem}
              progress={progress}
              loc={scaleLoc}
              next={next}
              isPreviewPanel
            />
          )
        }
      }
    } else if (entry.entryType === EntryType.MatrixEntryType) {
      // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if ((previewEntry as MatrixItem)?.matrixTitle) {
        return (
          <Matrix
            item={previewEntry as MatrixItem}
            progress={progress}
            loc={getMatrixMultiChoiceLoc(entry.entryItem)}
            next={next}
            isPreviewPanel
          />
        )
      }
    } else if (entry.entryType === EntryType.TextCardEntryType) {
      // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if ((previewEntry as TextCardItem)?.textCard.title) {
        return (
          <TextInstructions
            item={previewEntry as TextCardItem}
            progress={progress}
            loc={getTextInstructionsLoc(previewEntry as TextCardItem)}
            next={next}
            isPreviewPanel
          />
        )
      }
    } else if (entry.entryType === EntryType.TextHighlighterEntryType) {
      return (
        <TextHighlighter
          item={previewEntry as TextHighlighterItem}
          progress={progress}
          loc={{
            next: 'Next',
            instructions: 'Highlight all that apply'
          }}
          next={next}
          isPreviewPanel
        />
      )
    } else if (entry.entryType === EntryType.ConjointEntryType) {
      return (
        <ConjointQuestion
          item={previewEntry as ConjointItem}
          progress={progress}
          loc={getConjointLoc()}
          next={next}
          isPreviewPanel
        />
      )
    }
    return null
  }

  // Walkthrough: render only the current card (one at a time, advances on Next).
  // Guard against a stale index that's run past the end of the list.
  if (currentIndex >= renderableEntries.length) return null
  const currentEntry = renderableEntries[currentIndex]
  const currentNode = renderEntry(
    currentEntry,
    currentIndex,
    { current: currentIndex, total },
    {
      disabled: false,
      loading: false,
      onNext: () => handleOnNext(currentIndex)
    }
  )
  // Key by the entry so navigating between two same-type questions (e.g.
  // SingleChoice → SingleChoice) remounts the card rather than reusing the
  // instance — otherwise the previous question's component state (selected
  // option, slider position) leaks into the next.
  return currentNode
    ? React.cloneElement(currentNode, { key: currentEntry.id })
    : null
}

export default PreviewQuestions
